import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { prettyBytes } from '@repo/lib/prettyBytes';
import { FileText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Progress } from '@/components/ui/progress';

export function FileUploadList(fileUploadListProps: ComponentProps<'div'>) {
  const { className, ...props } = fileUploadListProps;
  return <div data-slot="file-upload-list" className={cn('grid gap-4', className)} {...props} />;
}

export function FileUploadListItem(fileUploadListItemProps: ComponentProps<'div'>) {
  const { className, ...props } = fileUploadListItemProps;
  return <div data-slot="file-upload-list-item" className={cn('border-border-primary bg-background grid gap-2 overflow-hidden rounded-md border p-2', className)} {...props} />;
}

export function FileUploadListHeader(fileUploadListHeaderProps: ComponentProps<'div'>) {
  const { className, ...props } = fileUploadListHeaderProps;
  return <div data-slot="file-upload-list-header" className={cn('flex items-center gap-3', className)} {...props} />;
}

export function FileUploadListIcon(fileUploadListIconProps: ComponentProps<typeof FeaturedIcon>) {
  const { className, children, ...props } = fileUploadListIconProps;
  return (
    <FeaturedIcon data-slot="file-upload-list-icon" className={cn(className)} {...props}>
      {children ?? <FileText />}
    </FeaturedIcon>
  );
}

export function FileUploadListInfo(fileUploadListInfoProps: ComponentProps<'div'>) {
  const { className, ...props } = fileUploadListInfoProps;
  return <div data-slot="file-upload-list-info" className={cn('grid flex-1 gap-0.5', className)} {...props} />;
}

export function FileUploadListName(fileUploadListNameProps: ComponentProps<'p'>) {
  const { className, ...props } = fileUploadListNameProps;
  return <p data-slot="file-upload-list-name" className={cn('text-foreground truncate text-sm font-medium tracking-tight whitespace-nowrap', className)} {...props} />;
}

export function FileUploadListDescription(fileUploadListDescriptionProps: ComponentProps<'div'>) {
  const { className, ...props } = fileUploadListDescriptionProps;
  return <div data-slot="file-upload-list-description" className={cn('text-muted-foreground flex items-center gap-1 truncate text-xs whitespace-nowrap', className)} {...props} />;
}

export function FileUploadListDescriptionSeparator(fileUploadListDescriptionSeparatorProps: ComponentProps<'span'>) {
  const { children, ...props } = fileUploadListDescriptionSeparatorProps;
  return (
    <span data-slot="file-upload-list-description-separator" {...props}>
      {children ?? '•'}
    </span>
  );
}

export function FileUploadListSize(fileUploadListSizeProps: ComponentProps<'span'> & { children: number }) {
  const { children, ...props } = fileUploadListSizeProps;
  return (
    <span data-slot="file-upload-list-size" {...props}>
      {prettyBytes(children)}
    </span>
  );
}

export function FileUploadListProgress(fileUploadListProgressProps: ComponentProps<typeof Progress>) {
  const { className, ...props } = fileUploadListProgressProps;
  return <Progress data-slot="file-upload-list-progress" className={cn('h-1', className)} {...props} />;
}

export function FileUploadListDescriptionText(fileUploadListDescriptionTextProps: ComponentProps<'span'>) {
  const { className, ...props } = fileUploadListDescriptionTextProps;
  return <span data-slot="file-upload-list-description-text" className={cn('flex items-center gap-1 truncate whitespace-nowrap [&>svg]:size-3', className)} {...props} />;
}

export function FileUploadListContent(fileUploadListContentProps: ComponentProps<'div'>) {
  const { ...props } = fileUploadListContentProps;
  return <div data-slot="file-upload-list-content" {...props} />;
}

export function FileUploadListActions(fileUploadListActionsProps: ComponentProps<'div'>) {
  const { className, ...props } = fileUploadListActionsProps;
  return <div data-slot="file-upload-list-actions" className={cn('flex items-center gap-1', className)} {...props} />;
}

export function FileUploadListAction(fileUploadListActionProps: ComponentProps<typeof Button>) {
  const { className, variant = 'ghost', size = 'sm', ...props } = fileUploadListActionProps;
  return <Button data-slot="file-upload-list-action" variant={variant} size={size} className={cn('', className)} {...props} />;
}
